<script lang="ts">
  import type { UpdateContent } from "./Context";
  // tiptap extensions
  import Placeholder from "@tiptap/extension-placeholder";
  import AI from "@tiptap-pro/extension-ai";
  import { FileHandler } from "./extensions/FileHandler";

  // custom extensions
  import Shortcut from "./extensions/KeyboardShortcuts/KeyboardShortcuts";
  import { toolSuggestion } from "./extensions/Mention/toolSuggestion";
  import CollaborationCursor from "@tiptap/extension-collaboration-cursor";
  import DragHandle from "./extensions/DragHandle/DragHandle";
  import Commands from "./extensions/SlashCommand/slashCommands";
  import MentionCommand from "./extensions/Mention/MentionCommand";
  import MentionSpaceCommand from "./extensions/Mention/MentionSpaceCommand";
  import suggestion from "./extensions/SlashCommand/suggestion";
  import Draggable from "./extensions/Draggable/Draggable";
  import { memberSuggestion, spaceSuggestion } from "./extensions/Mention/suggestion";
  import { getEditorContent } from "./utils";
  import {
    extensions as coreExtensions,
    type Content,
    type Extensions,
    type AnyExtension,
  } from "@tiptap/core";
  import { createEditor, Editor } from "svelte-tiptap";
  import { createEventDispatcher, onMount, tick } from "svelte";
  import { readOnlyExtensions, styles } from "./shared";
  import BubbleMenuBar from "./extensions/blocks/BubbleMenu/BubbleMenuBar.svelte";
  import ToolMentionCommand from "./extensions/Mention/ToolMentionCommand";
  import type { Readable } from "svelte/store";

  // styles
  import "./extensions/codeblock.css";

  import { page } from "$app/stores";
  import { Slice } from "@tiptap/pm/model";
  import { communitySlug } from "app/stores/community";
  import { getStores } from "$stores";
  import {
    getVersions,
    renderVersion as _renderVersion,
    renderVersionDiff as _renderVersionDiff,
    unrenderVersion,
  } from "./extensions/Collaboration/versions";
  import { Collaboration } from "./extensions/Collaboration/collaboration";
  import type { YdocStore } from "./ydoc";
  import type { User } from "$stores/user";
  import { env } from "$env/dynamic/public";

  export let content: Content = null;
  export let ydoc: YdocStore | null = null;
  export let editable = false;
  export let collab: boolean = true;
  export let readerMode: boolean = true;
  export let position: string = "absolute";
  export let placeholder: string = "";
  export let headingPlaceholder: string = "Untitled doc";
  export let forceHeading = false;
  export let autoFocus = false;
  export let advancedBlocks = false;
  export let inlineMentions = true;
  export let embedsBlocks = true;
  export let highlightColor: string = "";
  export let user: User;
  export let enableToolMentions = false;

  /* Focus the editor in special scenarios (for e.g. ReplyComposer) */
  export const focus = async () => {
    await tick();
    $editor.commands.focus();
  };

  /** Render a version in the editor bound to this composer */
  export const renderVersion = (i: number) =>
    $ydoc && _renderVersion($editor.view, getVersions($ydoc.doc), i);
  export const renderVersionDiff = (i: number) =>
    $ydoc && _renderVersionDiff($editor.view, getVersions($ydoc.doc), i);
  /** Reset editor back to rendering the current doc version in the editor bound to this composer */
  export const renderCurrent = () => unrenderVersion($editor.view);
  /** Get the current content of the editor */
  export const getContent = () => getEditorContent($editor);

  const dispatch = createEventDispatcher<{
    submit: UpdateContent;
    update: UpdateContent;
    create: UpdateContent;
  }>();

  let created = false;
  let aiMode = false;
  let editor: Readable<Editor>;
  let element: Element;

  let extensions: Extensions = [
    // PLACEHOLDER EXTENSIONS

    // placeholder for headings
    Placeholder.extend({ name: "placeholder-heading" }).configure({
      // @ts-ignore we want to return null to prevent overriding the default placeholder
      placeholder: ({ node }) => {
        if (node.type.name === "heading") {
          return `Heading ${node.attrs.level}`; // e.g. 'Heading 1'
        }
        // otherwise skip
        return;
      },
      showOnlyCurrent: false,
      includeChildren: true,
      emptyNodeClass: "is-empty !text-neutral-400 dark:!text-gray-400",
    }),

    // placeholder for top-level document titles (always visible)
    Placeholder.extend({ name: "placeholder-title" }).configure({
      // @ts-ignore we want to return null to prevent overriding the default placeholder
      placeholder: ({ node }) => {
        if (node.type.name === "title") {
          return headingPlaceholder;
        }
        // otherwise skip
        return;
      },
      showOnlyCurrent: false,
      includeChildren: true,
      emptyNodeClass: "is-empty !text-neutral-400 dark:!text-gray-400",
    }),

    // add always visible placeholder for accordion block
    Placeholder.extend({ name: "placeholder-accordion" }).configure({
      // @ts-ignore we want to return null to prevent overriding the default placeholder
      placeholder: ({ node }) => {
        if (node.type.name === "detailsSummary") {
          return "Accordion";
        }

        // otherwise skip
        return;
      },
      showOnlyCurrent: false,
      includeChildren: true,
      emptyNodeClass: "is-empty !text-neutral-400 dark:!text-gray-400",
    }),

    // default placeholder - for currently focused nodes
    Placeholder.configure({
      placeholder: ({ editor, node }) => {
        // skip node types that have placeholder overrides
        if (node.type.name === "title") return "";
        if (node.type.name === "heading") return "";
        if (node.type.name === "detailsSummary") return "";
        // show initial placeholder when empty
        if (placeholder && editor.isEmpty) {
          return placeholder;
        }
        // otherwise teach user
        return "Type '/' for commands";
      },
      includeChildren: true,
      showOnlyCurrent: true,
      emptyNodeClass: "is-empty !text-neutral-400 dark:!text-neutral-600 text-[13px]",
    }),

    // OTHER EXTENSIONS
    DragHandle.configure({ position }),
    Draggable,
    Commands.configure({
      suggestion: suggestion({
        inline: inlineMentions,
        advanced: advancedBlocks,
        embeds: embedsBlocks,
      }),
    }),
    MentionSpaceCommand.configure({ suggestion: spaceSuggestion(getStores()) }),
    ...(enableToolMentions
      ? [ToolMentionCommand.configure({ suggestion: toolSuggestion($communitySlug) })]
      : [MentionCommand.configure({ suggestion: memberSuggestion() })]),
    FileHandler({ user: user?.uuid!, community: $communitySlug }) as AnyExtension,
    Shortcut.configure({
      onSubmit: ({ editor }) => dispatch("submit", getEditorContent(editor)),
    }),
  ];

  // reactively update editable state
  $: $editor?.setEditable(editable);

  // update styles dynamically
  $: if ($editor) {
    $editor.setOptions({
      editorProps: {
        attributes: {
          class: `${styles} ${readerMode ? "max-w-none" : "m-auto"}`,
        },
      },
    });
  }

  onMount(() => {
    // enable ai mode for outverse folks
    const token = $page.data.aiToken;
    if (token && user?.email?.endsWith("@outverse.com")) {
      console.log(`Found API TOKEN: ${token}`);
      aiMode = true;
      console.log("Adding AI extension for outverse user");
      extensions = extensions.concat(
        // @ts-ignore
        AI.configure({
          appId: env.PUBLIC_TIPTAP_AI_APP_ID,
          autocompletion: true,
          token,
        })
      );
    }

    if (ydoc && $ydoc) {
      extensions = extensions.concat(
        Collaboration.configure({ document: $ydoc.doc, permanentUserData: $ydoc.permanentUserData })
      );

      if ($ydoc.webRTC && collab && user) {
        extensions = extensions.concat(
          CollaborationCursor.configure({
            provider: $ydoc.webRTC,
            user: {
              name: user.handle,
              color: user.profileColor,
              avatar: user.profileImage,
            },
          })
        );
      }
    }

    editor = createEditor({
      element,
      content,
      editable,
      autofocus: autoFocus,
      // override to fix issue with adding new line in contents block content
      enableCoreExtensions: false,
      extensions: (
        [
          coreExtensions.ClipboardTextSerializer,
          coreExtensions.Commands,
          coreExtensions.Editable,
          coreExtensions.FocusEvents,
          coreExtensions.Tabindex,
          coreExtensions.Keymap.extend({
            addKeyboardShortcuts() {
              const res = {
                ...this.parent?.(),
                Enter: () => {
                  return this.editor.commands.first(({ commands }) => [
                    () => commands.newlineInCode(),
                    () => commands.createParagraphNear(),
                    () => {
                      try {
                        return commands.liftEmptyBlock();
                      } catch {
                        return false;
                      }
                    },
                    () => commands.splitBlock(),
                  ]);
                },
              };
              return res;
            },
          }),
        ] as Extensions
      ).concat(extensions, readOnlyExtensions(!!ydoc, forceHeading, highlightColor)),

      editorProps: {
        attributes: {
          class: styles,
        },
        transformCopied(slice) {
          // @ts-ignore
          const frag = slice.content.content;
          if (frag && frag[0].type.name === "tableOfContents") {
            return new Slice(frag[0].content, 0, 0);
          }
          return slice;
        },
      },
      //
      // Editor Events
      //
      // triggered on every change
      onUpdate: ({ editor, transaction }) => {
        if (!created || !transaction.docChanged) return;
        dispatch("update", getEditorContent(editor));
      },
      onCreate: ({ editor }) => {
        created = true;
        dispatch("create", getEditorContent(editor));
      },
      onDestroy: () => {
        created = false;
      },
    });
  });
</script>

<BubbleMenuBar editor={$editor} {aiMode} />

<div class="editor" bind:this={element} />

<style lang="postcss">
  /* Basic editor styles */
  .editor :global .ProseMirror {
    pre {
      font-family: "JetBrainsMono", monospace;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;

      code {
        color: inherit;
        padding: 0;
        background: none;
        font-size: 0.8rem;
      }
    }

    .prose-sm {
      font-size: 13px;
    }

    code {
      word-break: break-word;
    }

    ul {
      list-style-type: disc;
    }
    ul ul {
      list-style-type: circle;
    }
    ul ul ul {
      list-style-type: square;
    }
    ul ul ul ul {
      list-style-type: disc;
    }
    ul ul ul ul ul {
      list-style-type: circle;
    }
    ul ul ul ul ul ul {
      list-style-type: square;
    }

    ol {
      list-style-type: decimal;
    }
    ol ol {
      list-style-type: lower-alpha;
    }
    ol ol ol {
      list-style-type: lower-roman;
    }
    ol ol ol ol {
      list-style-type: decimal;
    }
    ol ol ol ol ol {
      list-style-type: lower-alpha;
    }
    ol ol ol ol ol ol {
      list-style-type: lower-roman;
    }

    img {
      max-width: 100%;
      height: auto;
    }

    blockquote {
      padding-left: 1rem;
      border-left-color: #898e9b;
    }

    /* Give a remote user a caret */
    .collaboration-cursor__caret {
      border-left: 1px solid #0d0d0d;
      border-right: 1px solid #0d0d0d;
      margin-left: -1px;
      margin-right: -1px;
      pointer-events: none;
      position: relative;
      word-break: normal;
    }

    /* Render the username above the caret */
    .collaboration-cursor__label {
      border-radius: 3px 3px 3px 0;
      color: #0d0d0d;
      font-size: 12px;
      font-style: normal;
      font-weight: 600;
      left: -1px;
      line-height: normal;
      padding: 0.1rem 0.3rem;
      position: absolute;
      top: -1.4em;
      user-select: none;
      white-space: nowrap;
    }

    /* Placeholder (on every new line) */
    .is-empty::before {
      content: attr(data-placeholder);
      float: left;
      height: 0;
      pointer-events: none;
    }

    /* Note: Accordion styles also in Reader.svelte */
    /* Placeholder for accordion content */
    .accordion-content > .is-empty::before {
      content: "Add some content" !important; /* required to override placeholder */
      height: 0;
      float: left;
      pointer-events: none;
    }
    .accordion-content {
      opacity: 1;
      animation: fade-in 0.2s linear forwards;
    }

    /* Accordion toggle */
    .toggle-button {
      /* target direct child button only to avoid conflict with any other nested blocks in the toggle */
      > button:first-of-type {
        border-radius: 5px;
        margin-right: 6px;
        width: 28px;
        height: 28px;

        &:focus-visible {
          outline: 2px solid #8aa3fc;
        }

        &::before {
          display: flex;
          justify-content: center;
          align-items: center;
          content: "▸";
          width: 28px;
          height: 28px;
          transition: transform 0.2s ease-in-out;

          /* hover colours defined as utilities in app.css to be able to use with dark mode state */
        }
      }
    }

    .accordion {
      &.is-open > button:first-of-type::before {
        transform: rotate(90deg);
        transition: transform 0.2s ease-in-out;
      }

      > div {
        width: 100%;
        height: fit-content;
      }
    }

    /* Table block */
    /* border and text colours configured in app.css */
    table {
      border-collapse: separate;
      border-spacing: 0;
      margin: 0;
      overflow-y: hidden;
      table-layout: fixed;
      width: min-content; /* required to prevent table from stretching to full width of editor */

      /* header first cell */
      th:first-child {
        border-top-left-radius: 5px;
        border-bottom-left-radius: 5px;
      }

      th {
        text-align: left;
        font-weight: 500;
        z-index: 3;
        box-sizing: border-box;
        padding: 8px 4px;
        position: relative;
        vertical-align: top;

        > * {
          margin-bottom: 0;
        }
      }

      /* header last cell */
      th:last-child {
        border-top-right-radius: 5px;
        border-bottom-right-radius: 5px;
      }

      /* main table cells */
      td {
        box-sizing: border-box;
        position: relative;
        vertical-align: top;

        > * {
          margin-bottom: 0;
        }
      }

      p {
        margin: 0 4px;
      }

      .selectedCell:after {
        content: "";
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        pointer-events: none;
        position: absolute;
        z-index: 2;
      }

      .column-resize-handle {
        background-color: transparent;
        position: absolute;
        bottom: -2px;
        right: -2px;
        top: 0;
        width: 6px;
        height: 100%;
        cursor: ew-resize;
        z-index: 3;
      }
    }
    .resize-cursor {
      cursor: ew-resize;
    }

    .img-placeholder {
      position: relative;

      &:before {
        content: "";
        box-sizing: border-box;
        position: absolute;
        top: 40%;
        left: 40%;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 3px solid gray;
        border-top-color: white;
        animation: spinning 0.6s linear infinite;
      }
    }

    .video-placeholder {
      position: relative;

      &:before {
        content: "";
        box-sizing: border-box;
        position: absolute;
        top: 40%;
        left: 45%;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 3px solid gray;
        border-top-color: white;
        animation: spinning 0.6s linear infinite;
      }
    }

    .file-placeholder {
      position: relative;

      &:before {
        content: "";
        box-sizing: border-box;
        position: absolute;
        top: 20%;
        left: 45%;
        width: 36px;
        height: 36px;
        border-radius: 50%;
        border: 3px solid gray;
        border-top-color: white;
        animation: spinning 0.6s linear infinite;
      }
    }

    @keyframes spinning {
      to {
        transform: rotate(360deg);
      }
    }

    /* Version history diffing general styles */
    [ychange_type] {
      position: relative;
    }

    /* user names displayed over changed text on hover */
    .ychange-hover {
      display: none;
    }
    *:hover > .ychange-hover {
      display: inline;
      position: absolute;
      width: max-content;
      top: -14px;
      left: 0;
      font-size: 12px;
      user-select: none;
      word-break: normal;
    }

    ychange[ychange_type="removed"],
    p[ychange_type="removed"] {
      text-decoration: line-through;
      text-decoration-thickness: 0.5px;
      background-color: #ff000059;
    }
    p[ychange_type="removed"] > span > br {
      display: none;
    }
    *:not(ychange)[ychange_type="removed"] {
      background-color: #ff000059;
      color: inherit !important;
    }
    img[ychange_type="removed"] {
      padding: 2px;
    }

    /* this is handled by InlineMentionBlock for members, but keeping separate for now until deciding how to restructure */
    /* Tool mention styles */
    /* :global(.tool-mention) {
      background-color: rgba(147, 197, 253, 0.3);
      border-radius: 0.25rem;
      padding: 0.125rem 0.25rem;
      color: #2563eb;
      cursor: default;
      pointer-events: none;
      user-select: none;
    } */
  }
</style>
